import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { Room } from '@mui/icons-material';
import { TableCell, TableRow, Typography } from '@mui/material';
import { EventItem, useMatchsPlannerContext } from '@scorenco/components';
import { Types } from '@scorenco/core';
import { parseISO } from 'date-fns';
import { useRouter } from 'next/router';

import { GetEventPlayingTeamInfosDocument } from './MatchRowForm.query.generated';
import { createKeywords } from './utils';

type MatchRowFormProps = {
  event?: Types.Competitions_Event_Detail;
  searched?: string;
  club?: Types.Clubs_Club;
  convocationSettings: any;
};

export const MatchRowForm = ({
  event,
  searched,
  club,
  convocationSettings,
}: MatchRowFormProps) => {
  const router = useRouter();
  const { clubId, isLoading, tasks } = useMatchsPlannerContext();

  const isHome = event?.teams?.[0]?.is_team_of_club === 1;
  const teamInfoId = event?.playing_teams?.[0]?.info?.id;
  const showLicence = club?.planner_settings?.show_licence;

  const eventInfoQuery = useQuery(GetEventPlayingTeamInfosDocument, {
    variables: {
      teamInfoId: teamInfoId as number,
      isHome,
      showLicence,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !teamInfoId || !club,
    context: {
      role: 'anonymous',
    },
  });
  const teamInfo = eventInfoQuery.data?.competitions_playing_team_info_by_pk;

  const [keywords, setKeywords] = useState<string[] | undefined>([]);

  useEffect(() => {
    createKeywords(event, teamInfo, setKeywords);
  }, [teamInfo]);

  const placeName = (isHome && teamInfo?.equipment?.name) || event?.place?.name;

  const [refereeOfficial, setRefereeOfficial] = useState(
    teamInfo?.playing_team.team_in_season.team.referee_official &&
      teamInfo?.referees?.length === 0
  );
  const [scoringTableOfficial, setScoringTableOfficial] = useState(
    teamInfo?.playing_team.team_in_season.team.scoring_table_official &&
      teamInfo?.scoring_table_managers?.length === 0
  );

  useEffect(() => {
    setRefereeOfficial(
      teamInfo?.playing_team.team_in_season.team.referee_official &&
        teamInfo?.referees?.length === 0
    );
    setScoringTableOfficial(
      teamInfo?.playing_team.team_in_season.team.scoring_table_official &&
        teamInfo?.scoring_table_managers?.length === 0
    );
  }, [teamInfo?.playing_team.team_in_season.team.referee_official]);

  if (
    searched &&
    !keywords?.some(
      (keyword) =>
        keyword
          ?.toLocaleLowerCase('fr')
          ?.normalize('NFD')
          ?.replace(/[\u0300-\u036f]/g, '')
          ?.includes(
            searched
              ?.toLocaleLowerCase('fr')
              ?.normalize('NFD')
              ?.replace(/[\u0300-\u036f]/g, '')
          )
    )
  ) {
    return null;
  }

  return (
    <TableRow
      css={(theme) => css`
        border-bottom: 0.75px solid ${theme.vars.palette.divider};
      `}
    >
      <TableCell
        css={() => css`
          min-width: 30rem;
          max-width: 30rem;
          ${!teamInfo?.status && 'filter: grayscale(100%);'};
        `}
        align="center"
      >
        <EventItem
          event={event}
          clubId={clubId}
          showShortName={true}
          showDate={true}
        />
        {placeName && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontStyle: 'italic',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Room sx={{ fontSize: '1rem' }} />
            {placeName}
          </Typography>
        )}
      </TableCell>
      {teamInfo?.status ? (
        <>
          <TableCell>
            {teamInfo?.meeting_time
              ? parseISO(teamInfo?.meeting_time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Heure non-renseignée'}
          </TableCell>

          {!convocationSettings?.exclude_referee && (
            <TableCell
              css={(theme) => css`
                background: ${!isHome && 'rgba(224, 224, 224, 0.3)'};
                border-right: 0.75px solid ${theme.vars.palette.divider};
                border-left: 0.75px solid ${theme.vars.palette.divider};
              `}
            >
              {isHome && refereeOfficial ? (
                <Typography>👨‍💼 Arbitrage officiel</Typography>
              ) : (
                <ul style={{ margin: 0, padding: 0 }}>
                  {teamInfo?.referees?.map((member) => {
                    return (
                      <li
                        key={member.id}
                        style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                      >
                        {member?.member?.first_name}{' '}
                        {member?.member?.last_name?.toUpperCase()}
                        {club?.planner_settings.show_licence &&
                          ' - ' + member?.member?.licence}
                      </li>
                    );
                  })}
                </ul>
              )}
            </TableCell>
          )}

          {!convocationSettings?.exclude_scoring_table && (
            <TableCell
              css={(theme) => css`
                background: ${!isHome && 'rgba(224, 224, 224, 0.3)'};
                border-right: 0.75px solid ${theme.vars.palette.divider};
                border-left: 0.75px solid ${theme.vars.palette.divider};
              `}
            >
              {isHome && scoringTableOfficial ? (
                <Typography>👨‍💼 Table de marque officielle</Typography>
              ) : (
                <ul style={{ margin: 0, padding: 0 }}>
                  {teamInfo?.scoring_table_managers?.map((member) => {
                    return (
                      <li
                        key={member.id}
                        style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                      >
                        {member?.member?.first_name}{' '}
                        {member?.member?.last_name?.toUpperCase()}
                        {club?.planner_settings.show_licence &&
                          ' - ' + member?.member?.licence}
                      </li>
                    );
                  })}
                </ul>
              )}
            </TableCell>
          )}

          {!convocationSettings?.exclude_stadium_manager && (
            <TableCell
              css={(theme) => css`
                background: ${!isHome && 'rgba(224, 224, 224, 0.3)'};
                border-right: 0.75px solid ${theme.vars.palette.divider};
                border-left: 0.75px solid ${theme.vars.palette.divider};
              `}
            >
              {isHome && (
                <ul style={{ margin: 0, padding: 0 }}>
                  {teamInfo?.stadium_managers?.map((member) => {
                    return (
                      <li
                        key={member.id}
                        style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                      >
                        {member?.member?.first_name}{' '}
                        {member?.member?.last_name?.toUpperCase()}
                        {club?.planner_settings.show_licence &&
                          ' - ' + member?.member?.licence}
                      </li>
                    );
                  })}
                </ul>
              )}
            </TableCell>
          )}

          {!convocationSettings?.exclude_bar_manager && (
            <TableCell
              css={(theme) => css`
                background: ${!isHome && 'rgba(224, 224, 224, 0.3)'};
                border-right: 0.75px solid ${theme.vars.palette.divider};
                border-left: 0.75px solid ${theme.vars.palette.divider};
              `}
            >
              {isHome && (
                <ul style={{ margin: 0, padding: 0 }}>
                  {teamInfo?.bar_managers?.map((member) => {
                    return (
                      <li
                        key={member.id}
                        style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                      >
                        {member?.member?.first_name}{' '}
                        {member?.member?.last_name?.toUpperCase()}
                        {club?.planner_settings.show_licence &&
                          ' - ' + member?.member?.licence}
                      </li>
                    );
                  })}
                </ul>
              )}
            </TableCell>
          )}

          {tasks
            ?.filter(
              (task) =>
                !convocationSettings?.exclude_task_ids?.includes(task.id)
            )
            .map((task) => (
              <TaskCell
                key={task.id}
                task={task}
                isHome={isHome}
                teamInfo={teamInfo}
              />
            ))}
          <TableCell>
            <Typography variant="body2">{teamInfo?.public_comment}</Typography>
          </TableCell>
        </>
      ) : (
        !isLoading && (
          <td
            style={{
              paddingLeft: '1rem',
              textAlign: 'center',
            }}
            colSpan={5}
          >
            'Informations de matchs non disponibles pour le moment. 😢
          </td>
        )
      )}
    </TableRow>
  );
};

const TaskCell = ({ task, isHome, teamInfo, club }) => {
  const isTask =
    task.game_type === '' ||
    (task.game_type === 'home' && isHome) ||
    (task.game_type === 'away' && !isHome);

  const [isOfficial] = useState(
    teamInfo?.playing_team.team_in_season.team?.team_tasks?.find(
      (job) => job.task_id === task.id
    )?.officials &&
      teamInfo?.jobs?.find((t) => t.task.id === task.id)?.volunteers?.length ===
        0
  );

  return (
    <TableCell
      css={(theme) => css`
        background: ${!isTask && 'rgba(224, 224, 224, 0.3)'};
        border-right: 0.75px solid ${theme.vars.palette.divider};
        border-left: 0.75px solid ${theme.vars.palette.divider};
      `}
    >
      {isTask && isOfficial ? (
        <Typography>👨‍💼 Type de convocation officielle</Typography>
      ) : (
        <ul style={{ margin: 0, padding: 0 }}>
          {teamInfo?.jobs
            ?.find((t) => t.task.id === task.id)
            ?.volunteers?.map((volunteer) => {
              return (
                <li
                  key={volunteer.id}
                  style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                >
                  {volunteer?.member?.first_name}{' '}
                  {volunteer?.member?.last_name?.toUpperCase()}
                  {club?.planner_settings.show_licence &&
                    ' - ' + volunteer?.member?.licence}
                </li>
              );
            })}
        </ul>
      )}
    </TableCell>
  );
};
